'use client';

import * as Styled from './OrganizationSelector.styled';
import { useCommonAuthProvider, LoginWithRedirectOptions } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { useOrganization } from 'app/_providers/Organization';
import { useUser } from 'app/_providers/User';

import { ApartmentOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import truncate from 'lodash/truncate';
import { Route } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React from 'react';
import { isOrgOwner } from 'shared/src/authz/validators';
import { Membership } from 'shared/src/types';

import type { MenuProps } from 'antd';

interface ICreateOrganizationMenuItemsProps {
  currentTenantId: string;
  handleRedirect: (membership: Membership) => void;
  membershipsData: Membership[] | undefined;
  isMembershipsLoading: boolean;
}

const defaultSelectorItems: NonNullable<MenuProps['items']> = [
  {
    key: '0',
    label: <Link href={{ pathname: '/organizations' }}>Manage Organizations</Link>,
  },
  {
    type: 'divider',
  },
];

const ORGANIZATION_NAME_MAX_DISPLAY_LENGTH = 64;
const truncateOrganizationName = (organizationName: string): string =>
  truncate(organizationName, { length: ORGANIZATION_NAME_MAX_DISPLAY_LENGTH });

const createOrganizationMenuItems = ({
  membershipsData,
  isMembershipsLoading,
  currentTenantId,
  handleRedirect,
}: ICreateOrganizationMenuItemsProps): MenuProps['items'] => {
  if (isMembershipsLoading || !membershipsData) {
    return [];
  }

  return membershipsData.map((membership) => {
    const organizationName = truncateOrganizationName(
      membership.organization.organizationAlias || membership.organization.id || membership.tenant,
    );
    const isCurrentOrganization = membership.tenant === currentTenantId;

    const handleClick = (): void => {
      handleRedirect(membership);
    };

    return {
      key: organizationName,
      label: isCurrentOrganization
        ? (
          <Styled.InactiveSelectorItem>{organizationName}</Styled.InactiveSelectorItem>
        )
        : (
          <Styled.ActiveSelectorItem onClick={handleClick}>{organizationName}</Styled.ActiveSelectorItem>
        ),
    };
  });
};

const DropdownRender = (node: React.ReactNode) => (
  <Styled.DropdownScrollableMenu>{node}</Styled.DropdownScrollableMenu>
);

const CurrentOrganization: React.FC<{ organization?: string }> = ({ organization }) => (
  <Styled.CurrentOrganizationWraper>
    <ApartmentOutlined />

    <Styled.OrganizationName>{organization || 'Manage Organizations'}</Styled.OrganizationName>
  </Styled.CurrentOrganizationWraper>
);

type OrganizationSelecterProps = {
  isOEM: boolean;
};

export const OrganizationSelector: React.FC<OrganizationSelecterProps> = ({ isOEM }) => {
  const { cloudOrganizationsMaxOwned, cloudControlPlaneCdnUi, cloudMspEnableUiFeatures } = useFlagsProxy();
  const { organizationAlias: currentOrganizationAlias, organizationId: currentTenantId } = useOrganization();
  const { memberships: { data: membershipsData, isLoading: isMembershipsLoading } } = useUser();
  const { loginWithRedirect, user } = useCommonAuthProvider();
  const router = useRouter();

  const currentOrganizationName = currentOrganizationAlias || currentTenantId;
  const processedCurrentOrganizationName = currentOrganizationName
    ? truncateOrganizationName(currentOrganizationName)
    : undefined;
  const organizationsCount = membershipsData ? membershipsData.length : 0;
  const userOwnedOrganizationsCount = membershipsData?.filter((membership) => isOrgOwner(membership.roles)).length ?? 0;
  const canCreate = userOwnedOrganizationsCount < cloudOrganizationsMaxOwned;

  const showOrganizationSelector = (canCreate || organizationsCount > 1) && !isOEM;

  const handleRedirect = async (membership: Membership): Promise<void> => {
    const { tenant, connections, invalidSsoMembership } = membership;
    const orgRedirectPath = cloudControlPlaneCdnUi && process.env.NEXT_PUBLIC_MODE !== 'preview'
      ? `https://app.${process.env.SHARED_DOMAIN}/organizations/${tenant}`
      : `/organizations/${tenant}`;

    if (cloudMspEnableUiFeatures) {
      if (invalidSsoMembership) {
        router.push(`/migrate-sso-membership?returnTo=${tenant}`);
      } else {
        const loginArgs: LoginWithRedirectOptions = {
          organizationId: tenant,
          returnTo: orgRedirectPath,
        };

        if (connections && connections.length > 0) {
          loginArgs['connection'] = connections[0];
          loginArgs['loginHint'] = user?.email;
        }
        loginWithRedirect(loginArgs);
      }
    } else {
      router.push(orgRedirectPath as Route);
    }
  };

  const organizationItems =
    createOrganizationMenuItems({
      membershipsData,
      isMembershipsLoading,
      currentTenantId,
      handleRedirect,
    }) ?? [];

  const items = [...defaultSelectorItems, ...organizationItems];

  return (
    <Styled.OrganizationSwitcher>
      {showOrganizationSelector
        ? (
          <Dropdown
            menu={{ items }}
            trigger={['click']}
            placement='bottomLeft'
            popupRender={DropdownRender}
          >
            <Styled.CurrentItemWrapper>
              <CurrentOrganization organization={processedCurrentOrganizationName} />
            </Styled.CurrentItemWrapper>
          </Dropdown>
        )
        : (
          <CurrentOrganization organization={processedCurrentOrganizationName} />
        )}
    </Styled.OrganizationSwitcher>
  );
};
